import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Target, ChevronRight, ClipboardList } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/shared/PageTitle';
import {
  useGetMyAssignedProspectsQuery,
  useGetMyAssignedPromotersQuery,
} from '../../store/teamMemberApi';

const MyAssignments: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: prospects = [], isLoading: prospectsLoading } = useGetMyAssignedProspectsQuery({});
  const { data: promoters = [], isLoading: promotersLoading } = useGetMyAssignedPromotersQuery();

  const isLoading = prospectsLoading || promotersLoading;

  return (
    <div className="space-y-6">
      <PageTitle title={t('teams.myAssignments.pageTitle')} />

      <div className="flex items-center gap-3">
        <ClipboardList className="w-7 h-7 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('teams.myAssignments.heading')}</h1>
          <p className="text-gray-500 mt-0.5 text-sm">
            {t('teams.myAssignments.subtitle')}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border shadow-sm p-12 text-center text-gray-500">
          {t('teams.myAssignments.loading')}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Prospects */}
          <div className="bg-white rounded-xl border shadow-sm">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                <h2 className="font-semibold text-gray-900">{t('teams.myAssignments.prospectsHeading')}</h2>
              </div>
              <span className="text-xs text-gray-500">{t('teams.myAssignments.prospectsAssigned', { count: prospects.length })}</span>
            </div>
            {prospects.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                {t('teams.myAssignments.noProspects')}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {prospects.map((p: any) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/prospects/${p.id}`)}
                      className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 text-left"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 text-sm truncate">
                          {p.first_name} {p.last_name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {p.company || p.email}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Promoters */}
          <div className="bg-white rounded-xl border shadow-sm">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                <h2 className="font-semibold text-gray-900">{t('teams.myAssignments.promotersHeading')}</h2>
              </div>
              <span className="text-xs text-gray-500">{t('teams.myAssignments.promotersAssigned', { count: promoters.length })}</span>
            </div>
            {promoters.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                {t('teams.myAssignments.noPromoters')}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {promoters.map((p: any) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/promoters/${p.id}`)}
                      className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 text-left"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 text-sm truncate">
                          {p.firstName} {p.lastName}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {p.company || p.email}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAssignments;
